import { useState } from 'react'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Col from 'react-bootstrap/Col'
import ListGroup from 'react-bootstrap/ListGroup'
import Row from 'react-bootstrap/Row'
import Spinner from 'react-bootstrap/Spinner'
import { toast } from 'react-toastify'
import { createMcpToken, revokeMcpToken, type McpToken } from '../../../types'
import { actionApiCall, formSubmit } from '../api/actionApiCall'
import { SubmitButton, TextField, useFormState } from '../components/form'
import { useCapabilities } from '../hooks/useCapabilities'
import { useInvalidateMcpTokens, useMcpTokens } from '../hooks/useMcpTokens'
import { formatDateTime } from '../utils/format'

/**
 * Přístup pro AI asistenta přes MCP (Model Context Protocol).
 *
 * Asistent (Claude Code, Claude Desktop…) se k aplikaci připojí tokenem
 * odsud a pak umí účtenky, které mu uživatel předá, přečíst, rozdělit mezi
 * členy skupiny a založit z nich útraty. Vidí přitom stejné skupiny jako
 * uživatel, kterému token patří.
 */
export function McpAccessPage() {
    const { data: capabilities } = useCapabilities()
    const { data: tokeny, isLoading } = useMcpTokens()
    const invalidate = useInvalidateMcpTokens()

    // Celý token vrací server jen jednou - drží se jen do opuštění stránky.
    const [novy, setNovy] = useState<McpToken | undefined>()

    const adresa = `${window.location.origin}/api/mcp`

    const form = useFormState({
        initialValues: { name: '' },
        onSubmit: async (values) => {
            const { data, fieldErrors } = await formSubmit(() => createMcpToken({ body: { name: values.name.trim() } }), {
                successMessage: 'Token byl vytvořen.',
            })
            if (!data) {
                return { fieldErrors }
            }
            setNovy(data)
            form.reset()
            await invalidate()
        },
    })

    const zrus = async (token: McpToken) => {
        if (!window.confirm(`Opravdu zrušit token „${token.name}"? Asistent, který ho používá, se odpojí.`)) {
            return
        }
        const vysledek = await actionApiCall(() => revokeMcpToken({ path: { tokenId: token.id } }), {
            successMessage: 'Token byl zrušen.',
        })
        if (vysledek !== undefined) {
            if (novy?.id === token.id) {
                setNovy(undefined)
            }
            await invalidate()
        }
    }

    return (
        <Row className="justify-content-center">
            <Col xs={12} md={10} lg={8}>
                <Card className="mt-3">
                    <Card.Body>
                        <h1 className="h4 mb-3">AI asistent (MCP)</h1>

                        <p className="text-secondary small">
                            Účtenky za vás může přečíst a rozdělit AI asistent — Claude Code, Claude Desktop nebo jiná
                            aplikace s podporou MCP. Připojí se tokenem odsud a vidí stejné skupiny jako vy. U nahrávání
                            účtenky pak zapněte <strong>Zpracovat přes AI asistenta (MCP)</strong> a asistenta požádejte
                            třeba „Zpracuj moje účtenky ve SplitUpu".
                        </p>

                        {capabilities && !capabilities.receiptMcp && (
                            <Alert variant="secondary" className="py-2 small">
                                MCP není v této instalaci zapnuté. Správce ho zapne proměnnou prostředí <code>MCP_ENABLED=true</code>.
                            </Alert>
                        )}

                        {capabilities?.receiptMcp && (
                            <form onSubmit={form.handleSubmit} noValidate>
                                <TextField
                                    label="Název nového tokenu"
                                    value={form.values.name}
                                    onChange={(value) => form.setValue('name', value)}
                                    error={form.fieldErrors.name}
                                    placeholder="Např. Claude Code na notebooku"
                                    helpText="Podle názvu poznáte, který token patří kterému asistentovi."
                                    maxLength={100}
                                    autoComplete="off"
                                />
                                <SubmitButton submitting={form.submitting}>Vytvořit token</SubmitButton>
                            </form>
                        )}
                    </Card.Body>
                </Card>

                {novy?.token && <NovyToken token={novy.token} adresa={adresa} onHide={() => setNovy(undefined)} />}

                <Card className="mt-3">
                    <Card.Header>Vaše tokeny</Card.Header>
                    {isLoading && (
                        <Card.Body className="text-secondary small">
                            <Spinner animation="border" size="sm" className="me-2" />
                            Načítám tokeny…
                        </Card.Body>
                    )}
                    {!isLoading && (tokeny ?? []).length === 0 && (
                        <Card.Body className="text-secondary small">Zatím nemáte žádný token.</Card.Body>
                    )}
                    {(tokeny ?? []).length > 0 && (
                        <ListGroup variant="flush" data-testid="mcp-tokeny">
                            {(tokeny ?? []).map((token) => (
                                <ListGroup.Item key={token.id} className="d-flex align-items-center gap-2">
                                    <div className="flex-grow-1 min-w-0">
                                        <div className="fw-semibold text-truncate">{token.name}</div>
                                        <div className="small text-secondary">
                                            <code>{token.tokenPrefix}…</code> · vytvořen {formatDateTime(token.createdAt)}
                                        </div>
                                        <div className="small text-secondary">
                                            {token.lastUsedAt ? `Naposledy použit ${formatDateTime(token.lastUsedAt)}` : 'Zatím nepoužit'}
                                        </div>
                                    </div>
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        className="su-tappable flex-shrink-0"
                                        onClick={() => void zrus(token)}
                                    >
                                        Zrušit
                                    </Button>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    )}
                </Card>
            </Col>
        </Row>
    )
}

type NovyTokenProps = {
    token: string
    adresa: string
    onHide: () => void
}

/** Právě vytvořený token s návodem, jak ho zadat do asistenta. */
function NovyToken({ token, adresa, onHide }: NovyTokenProps) {
    const claudeCode = `claude mcp add --transport http splitup ${adresa} --header "Authorization: Bearer ${token}"`
    const konfigurace = JSON.stringify(
        { mcpServers: { splitup: { type: 'http', url: adresa, headers: { Authorization: `Bearer ${token}` } } } },
        null,
        2,
    )
    // Claude Desktop vzdálené servery s vlastní hlavičkou připojuje přes mcp-remote.
    const claudeDesktop = JSON.stringify(
        {
            mcpServers: {
                splitup: {
                    command: 'npx',
                    args: ['mcp-remote', adresa, '--header', 'Authorization:${SPLITUP_AUTH}'],
                    env: { SPLITUP_AUTH: `Bearer ${token}` },
                },
            },
        },
        null,
        2,
    )

    return (
        <Card className="mt-3 border-success" data-testid="novy-mcp-token">
            <Card.Body>
                <h2 className="h6">Nový token</h2>
                <Alert variant="warning" className="py-2 small">
                    Token se zobrazí jen teď. Uložte si ho do asistenta — později už ho znovu neuvidíte, jen zrušit.
                </Alert>

                <Ukazka nadpis="Token" text={token} />
                <Ukazka nadpis="Claude Code" text={claudeCode} />
                <Ukazka nadpis="Claude Desktop (claude_desktop_config.json)" text={claudeDesktop} />
                <Ukazka nadpis="Jiné aplikace s podporou MCP přes HTTP" text={konfigurace} />

                <Button variant="outline-secondary" className="su-tappable" onClick={onHide}>
                    Mám uloženo, skrýt
                </Button>
            </Card.Body>
        </Card>
    )
}

function Ukazka({ nadpis, text }: { nadpis: string; text: string }) {
    const zkopiruj = async () => {
        try {
            await navigator.clipboard.writeText(text)
            toast.success('Zkopírováno do schránky.')
        } catch {
            // Schránka je dostupná jen přes HTTPS; jinak zbývá označit text ručně.
            toast.info('Zkopírovat se nepodařilo, označte text ručně.')
        }
    }

    return (
        <div className="mb-3">
            <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
                <span className="small fw-semibold">{nadpis}</span>
                <Button variant="link" size="sm" className="p-0" onClick={() => void zkopiruj()}>
                    <i className="bi bi-clipboard me-1" aria-hidden="true" />
                    Kopírovat
                </Button>
            </div>
            <pre className="bg-body-tertiary border rounded p-2 small mb-0 text-wrap text-break">{text}</pre>
        </div>
    )
}
